import { app, authentication } from '@microsoft/teams-js';

let initialized = false;
let initPromise = null;
let isInTeamsFlag = null;

/**
 * Race un promise contra un timeout. Rechaza con Error('__timeout__') si excede.
 */
const withTimeout = (promise, ms, tag = 'op') =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`__timeout__:${tag}`)), ms))
  ]);

/**
 * Inicializa el SDK de Teams. Si en 8s no responde, asumimos que NO estamos
 * dentro de Teams (o Teams no puede inicializar la webview) y seguimos en
 * modo standalone. Esto evita la pantalla eternamente en "Inicializando".
 */
export async function initTeams() {
  if (initialized) return isInTeamsFlag;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      await withTimeout(app.initialize(), 8000, 'init');
      initialized = true;
      isInTeamsFlag = true;
      return true;
    } catch (err) {
      console.warn('[teams] initialize fallo o timeout:', err?.message || err);
      initialized = true;   // marcamos como intentado para no volver a colgar
      isInTeamsFlag = false;
      return false;
    }
  })();

  return initPromise;
}

export function isInTeams() {
  return isInTeamsFlag === true;
}

export async function getTeamsContext() {
  const inTeams = await initTeams();
  if (!inTeams) return null;
  try {
    return await withTimeout(app.getContext(), 5000, 'context');
  } catch (err) {
    console.warn('[teams] getContext fallo:', err?.message || err);
    return null;
  }
}

/**
 * Obtiene un token JWT de Entra ID via Teams SSO.
 * Devuelve null si no estamos en Teams (modo browser standalone).
 */
export async function getSsoToken() {
  const inTeams = await initTeams();
  if (!inTeams) return null;
  try {
    return await withTimeout(authentication.getAuthToken(), 10000, 'sso');
  } catch (err) {
    const msg = err?.message || String(err);
    if (msg.includes('__timeout__')) {
      throw new Error('El inicio de sesion (SSO) no respondio en 10s. Cierra y vuelve a abrir Teams.');
    }
    throw err;
  }
}

/**
 * Devuelve el tipo de cliente: desktop | web | android | ios | standalone
 */
export async function getHostClientType() {
  const ctx = await getTeamsContext();
  return ctx?.app?.host?.clientType || (isInTeams() ? 'unknown' : 'standalone');
}

/**
 * Error tipado para geolocalizacion.
 */
export class LocationError extends Error {
  constructor(kind, message, source, hint) {
    super(message);
    this.name = 'LocationError';
    this.kind = kind;     // 'denied' | 'unavailable' | 'timeout' | 'unsupported' | 'unknown'
    this.source = source; // 'teams' | 'browser' | 'both'
    this.hint = hint || null; // instrucciones adicionales para el usuario
  }
}

const TEAMS_LOC_TIMEOUT_MS  = 8000;
const HIGH_ACC_TIMEOUT_MS   = 6000;
const LOW_ACC_TIMEOUT_MS    = 20000;
const IP_FALLBACK_TIMEOUT_MS = 4000;

/**
 * Solicita ubicacion con 3 niveles:
 *  1) Teams geoLocation (con hasPermission/requestPermission) — 12s
 *  2) navigator alta precision — 10s
 *  3) navigator baja precision — 30s (cache 60s)
 *
 * Windows Desktop con Location Service apagado suele fallar en (1) y (2),
 * y a veces tambien en (3). Damos mensajes accionables.
 */
export async function requestLocation(onProgress) {
  const notify = typeof onProgress === 'function' ? onProgress : () => {};
  const inTeams = await initTeams();
  notify('locating');

  // -------- 1) Teams API --------
  let teamsErr = null;
  if (inTeams) {
    try {
      const teamsMod = await import('@microsoft/teams-js');
      const geo = teamsMod.geoLocation;
      if (geo && typeof geo.isSupported === 'function' && geo.isSupported()) {
        // Pedir permiso si el SDK expone el API
        try {
          if (typeof geo.hasPermission === 'function') {
            const has = await withTimeout(geo.hasPermission(), 4000, 'hasperm');
            if (!has && typeof geo.requestPermission === 'function') {
              await withTimeout(geo.requestPermission(), 8000, 'reqperm');
            }
          }
        } catch (permErr) {
          console.warn('[location] Teams permission chequeo fallo:', permErr?.message || permErr);
          // seguimos e intentamos getCurrentLocation igual
        }

        const loc = await withTimeout(geo.getCurrentLocation(), TEAMS_LOC_TIMEOUT_MS, 'teamsgeo');
        if (loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
          return {
            latitude: loc.latitude,
            longitude: loc.longitude,
            source: 'teams',
            accuracy: loc.accuracy || null
          };
        }
        teamsErr = new Error('Teams geoLocation devolvio null');
      }
    } catch (err) {
      teamsErr = err;
      console.warn('[location] Teams API fallo:', err?.message || err);
    }
  }

  // -------- 2) navigator --------
  if (!navigator.geolocation) {
    throw new LocationError(
      'unsupported',
      'Este navegador no soporta geolocalizacion. Abre la app en Teams movil o Teams web (Chrome/Edge).',
      teamsErr ? 'both' : 'browser'
    );
  }

  const tryBrowser = (opts) => new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        source: 'browser',
        accuracy: pos.coords.accuracy || null
      }),
      (err) => reject(err),
      opts
    );
  });

  const platformHint = detectPlatformHint();

  // Con maximumAge alto (5 min) reusamos cache del navegador si existe — ideal
  // para Windows donde el fix inicial es lento pero luego queda cacheado.
  try {
    return await tryBrowser({ enableHighAccuracy: true, timeout: HIGH_ACC_TIMEOUT_MS, maximumAge: 300000 });
  } catch (err1) {
    console.warn('[location] Alta precision fallo:', err1?.code, err1?.message);

    if (err1 && err1.code === 1) {
      throw new LocationError(
        'denied',
        'Permisos de ubicacion rechazados. Autoriza el acceso para poder marcar.',
        'browser',
        platformHint.denied
      );
    }

    try {
      return await tryBrowser({ enableHighAccuracy: false, timeout: LOW_ACC_TIMEOUT_MS, maximumAge: 300000 });
    } catch (err2) {
      console.warn('[location] Baja precision fallo:', err2?.code, err2?.message);
      const code = err2?.code || err1?.code;

      if (code === 1) {
        throw new LocationError(
          'denied',
          'Permisos de ubicacion rechazados. Autoriza el acceso para poder marcar.',
          'browser',
          platformHint.denied
        );
      }

      // Ultimo recurso: geolocalizacion por IP para desbloquear la marca.
      // Es aproximada (precision ~ciudad), pero deja constancia y permite marcar.
      // El backend la registrara como 'remoto' por la distancia.
      try {
        notify('locating-ip');
        const ipLoc = await ipGeolocate();
        console.info('[location] Fallback IP-based exitoso:', ipLoc);
        return ipLoc;
      } catch (ipErr) {
        console.warn('[location] Fallback IP fallo:', ipErr?.message || ipErr);
      }

      if (code === 2) {
        throw new LocationError(
          'unavailable',
          'Windows no puede obtener tu ubicacion.',
          'browser',
          platformHint.unavailable
        );
      }
      if (code === 3) {
        throw new LocationError(
          'timeout',
          'La ubicacion tarda demasiado en responder.',
          'browser',
          platformHint.timeout
        );
      }
      throw new LocationError(
        'unknown',
        `No pudimos obtener tu ubicacion (${err2?.message || 'error desconocido'}).`,
        'browser',
        'Si el problema persiste, avisa a TI o marca desde tu celular.'
      );
    }
  }
}

/**
 * Fallback por IP publica. Usa ipapi.co (sin API key). Precision ~ciudad,
 * util solo para desbloquear la marca cuando el GPS de Windows no responde.
 */
async function ipGeolocate() {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), IP_FALLBACK_TIMEOUT_MS);
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const lat = Number(data?.latitude);
    const lng = Number(data?.longitude);
    if (!isFinite(lat) || !isFinite(lng)) {
      throw new Error('respuesta sin lat/lng');
    }
    return {
      latitude: lat,
      longitude: lng,
      source: 'ip',
      accuracy: 25000 // ~25km aproximado
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Detecta plataforma para dar instrucciones accionables.
 */
function detectPlatformHint() {
  const ua = (navigator.userAgent || '').toLowerCase();
  const isWin = ua.includes('windows');
  const isMac = ua.includes('mac os');
  const isAndroid = ua.includes('android');
  const isIOS = /iphone|ipad|ipod/.test(ua);

  if (isWin) {
    return {
      denied:      'Configuracion de Windows > Privacidad y seguridad > Ubicacion. Activa "Servicios de ubicacion" y permitele el acceso a Microsoft Teams y al navegador.',
      unavailable: 'Ve a Configuracion de Windows > Privacidad y seguridad > Ubicacion y enciende "Servicios de ubicacion". Si esta encendido, prueba marcar desde tu celular (Teams movil detecta la ubicacion mas rapido).',
      timeout:     'En Windows la ubicacion suele tardar. Reintenta cerca de una ventana, o marca desde Teams en tu celular donde el GPS responde en segundos.'
    };
  }
  if (isMac) {
    return {
      denied:      'Preferencias del sistema > Privacidad y seguridad > Servicios de localizacion. Habilita Teams y el navegador.',
      unavailable: 'Habilita "Servicios de localizacion" en Preferencias del sistema > Privacidad y seguridad. Si esta encendido, marca desde tu celular.',
      timeout:     'La ubicacion tarda. Reintenta cerca de una ventana o marca desde Teams movil.'
    };
  }
  if (isAndroid) {
    return {
      denied:      'Ajustes de Android > Ubicacion. Activa la ubicacion y permitele acceso a Teams.',
      unavailable: 'Enciende la ubicacion en Ajustes de Android y ponte al aire libre por unos segundos.',
      timeout:     'El GPS esta tomando mas tiempo del normal. Sal al aire libre o cerca de una ventana y reintenta.'
    };
  }
  if (isIOS) {
    return {
      denied:      'Ajustes de iPhone > Privacidad > Localizacion. Activa la localizacion y permitele acceso a Teams.',
      unavailable: 'Enciende la localizacion en Ajustes > Privacidad. Cerca de una ventana funciona mejor.',
      timeout:     'El GPS esta tardando. Cerca de una ventana o al aire libre responde mas rapido.'
    };
  }
  return {
    denied:      'Autoriza el acceso a tu ubicacion en la configuracion del sistema y del navegador.',
    unavailable: 'Verifica que la ubicacion del sistema este encendida, o marca desde tu celular.',
    timeout:     'La ubicacion no responde. Reintenta o marca desde Teams movil.'
  };
}
